import {
  Box,
  Center,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAppDispatch, useTypedSelector } from '../../../../hooks/store';
import { ReactComponent as Close } from '../../../../images/icons/icon-close.svg';
import { ReactComponent as Search } from '../../../../images/icons/icon-search.svg';
import { ReactComponent as SearchHistory } from '../../../../images/icons/search-history.svg';

import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import {
  changeKeywordsSearchVideoLibrary,
  selectSearchVideoKeywords,
} from '../../../../slices/videoLibrary';

const SearchVideoLibrary = () => {
  const intl = useIntl();
  const [inputSearch, setInputSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const seachVideoKeywords = useTypedSelector(selectSearchVideoKeywords);

  const ref = useRef<HTMLInputElement>(null);
  const onChangeInputSearch = useCallback((search: string) => {
    setInputSearch(search);
    setIsOpen(true);
  }, []);

  const submitSearch = useCallback(() => {
    if (inputSearch) {
      setInputSearch('');
      navigate(`/video-library/search-video?keyword=${inputSearch}`);
      if (!seachVideoKeywords.includes(inputSearch)) {
        dispatch(
          changeKeywordsSearchVideoLibrary([...seachVideoKeywords, inputSearch])
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputSearch, seachVideoKeywords]);

  const onDeleteKeywords = useCallback(() => {
    dispatch(
      changeKeywordsSearchVideoLibrary(
        seachVideoKeywords.filter((i) => i === inputSearch)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputSearch, seachVideoKeywords]);

  const searchDataByKeywords = useCallback(
    (e: any) => {
      if (e.key === 'Enter') {
        submitSearch();
      }
    },
    [submitSearch]
  );

  useEffect(() => {
    window.addEventListener('keydown', searchDataByKeywords);

    return () => {
      window.removeEventListener('keydown', searchDataByKeywords);
    };
  });

  const arraySearchCurrent = useMemo(() => {
    let array: string[] = [];
    if (seachVideoKeywords.length > 0 && inputSearch) {
      array = _.uniq(
        seachVideoKeywords.filter((i) =>
          i.toLowerCase().match(inputSearch.toLowerCase())
        )
      );
    }
    return array;
  }, [inputSearch, seachVideoKeywords]);

  const onClickSearchHistory = useCallback((i: string) => {
    setInputSearch(i);
    setIsOpen(false);
    ref.current && ref.current.focus();
  }, []);

  return (
    <Popover
      placement={'bottom'}
      isOpen={isOpen && arraySearchCurrent.length > 0 ? true : false}
      onClose={() => setIsOpen(false)}
      initialFocusRef={ref}
    >
      <PopoverTrigger>
        <InputGroup
          sx={{ path: { fill: '#C0C0C0' }, sgv: { marginTop: '3px' } }}
          cursor={'pointer'}
        >
          <Input
            ref={ref}
            value={inputSearch}
            placeholder={intl.formatMessage({ id: 'SearchByKeywords' })}
            _focus={{ outline: `none` }}
            _hover={{ outline: `none`, borderColor: 'transparent' }}
            border={'unset'}
            h={{ mobile: '36px', sm: `44px` }}
            style={{
              boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.1)',
              borderRadius: `8px`,

              letterSpacing: `0.01em`,
              color: `#8A8A8D`,
              outline: `none`,
              background: `#fff`,
            }}
            fontSize={{ mobile: 13, sm: 15 }}
            lineHeight={{ mobile: '18px', sm: '22px' }}
            w={{ mobile: '100%' }}
            paddingLeft={'44px !important'}
            rounded={'100px !important'}
            width={'100%'}
            minW={{ mobile: '100%', md: '300px', xl: '400px' }}
            onChange={(event) => onChangeInputSearch(event.target.value)}
          />
          <InputLeftElement
            left={'4px'}
            children={<Search />}
            h={{ mobile: '36px', sm: `44px` }}
            sx={{
              svg: {
                width: { mobile: '16px', sm: '20px' },
                height: { mobile: '16px', sm: '20px' },
              },
              path: {
                fill: '#54565B',
              },
            }}
          />
        </InputGroup>
      </PopoverTrigger>
      <PopoverContent
        rounded={20}
        padding={{ mobile: '16px', sm: '24px 16px' }}
        _focus={{ outline: 'none' }}
        border={'none'}
        width={{ mobile: 'calc(100vw - 32px)' }}
        maxW={{ mobile: '100%', md: '300px', xl: '400px' }}
        sx={{
          '::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        mt={'4px !important'}
      >
        <PopoverBody
          p={{ mobile: '0px' }}
          _focus={{ outline: 'none' }}
          width={'100%'}
        >
          <VStack spacing={'8px'} alignItems={'flex-start'}>
            <Box fontSize={16} lineHeight={'24px'} color={'#8A8A8D'}>
              {intl.formatMessage({ id: 'RecentSearchs' })}
            </Box>
            {arraySearchCurrent.map((i, idx) => (
              <Flex
                key={idx}
                justifyContent={'space-between'}
                fontSize={15}
                lineHeight={'22px'}
                color={'#54565B'}
                width={'100%'}
                alignItems={'center'}
              >
                <HStack
                  spacing={'6px'}
                  cursor={'pointer'}
                  onClick={() => onClickSearchHistory(i)}
                >
                  <SearchHistory />
                  <Box>{i}</Box>
                </HStack>
                <Center
                  cursor={'pointer'}
                  onClick={onDeleteKeywords}
                  sx={{
                    path: { fill: '#494B51' },
                  }}
                  boxSize={'12px'}
                >
                  <Close />
                </Center>
              </Flex>
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default SearchVideoLibrary;
